/**
 * Situação da empresa no Clube Lidera.
 *
 * O provisionamento é disparado pelo cadastro de colaborador e, até agora, todo desfeito ruim
 * terminava num aviso no log do servidor: integração desligada, empresa sem e-mail, colaborador
 * sem e-mail ficando de fora do lote, clube fora do ar. Quem opera via a empresa cadastrada aqui
 * e supunha que ela estava lá.
 *
 * Este bloco responde três perguntas nessa ordem: chegou? o que impede? quem ficou de fora?
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as OkIcon,
  ErrorOutline as FalhouIcon,
  PauseCircleOutline as PendenteIcon,
  Groups as PessoasIcon,
} from '@mui/icons-material';

import { ProfileSection } from '../../../../components/common';
import {
  integrationCredentialsService,
  type SituacaoNoClubeLidera,
} from '../../../../services/integrationCredentialsService';

interface Props {
  companyId: string;
}

function dataLegivel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

export const ClubeLideraStatusCard: React.FC<Props> = ({ companyId }) => {
  const [situacao, setSituacao] = useState<SituacaoNoClubeLidera | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setSituacao(await integrationCredentialsService.clubeLidera(companyId));
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível consultar o Clube Lidera');
    } finally {
      setCarregando(false);
    }
  }, [companyId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (carregando) {
    return (
      <ProfileSection title="Clube Lidera" icon={<PessoasIcon />}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      </ProfileSection>
    );
  }

  if (erro || !situacao) {
    return (
      <ProfileSection title="Clube Lidera" icon={<PessoasIcon />}>
        <Alert severity="error">{erro ?? 'Sem dados'}</Alert>
      </ProfileSection>
    );
  }

  const { ultimoEnvio } = situacao;
  const nuncaTentou = ultimoEnvio.status === null;

  const chip =
    ultimoEnvio.status === 'enviado' ? (
      <Chip size="small" color="success" icon={<OkIcon />} label={`Enviado em ${dataLegivel(ultimoEnvio.em)}`} />
    ) : ultimoEnvio.status === 'falhou' ? (
      <Chip size="small" color="error" icon={<FalhouIcon />} label={`Falhou em ${dataLegivel(ultimoEnvio.em)}`} />
    ) : ultimoEnvio.status === 'nao_enviado' ? (
      <Chip size="small" color="warning" icon={<PendenteIcon />} label={`Não enviado em ${dataLegivel(ultimoEnvio.em)}`} />
    ) : (
      <Chip size="small" variant="outlined" icon={<PendenteIcon />} label="Nunca houve tentativa" />
    );

  return (
    <ProfileSection title="Clube Lidera" icon={<PessoasIcon />}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A empresa e seus colaboradores vão para o clube sozinhos: no cadastro de colaborador e,
        a cada 30 minutos, numa rotina que reprocessa o que ficou pendente. Nada aqui precisa ser
        disparado à mão.
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        {chip}
        <Chip
          size="small"
          variant="outlined"
          label={`${situacao.ativos} ativo(s) · ${situacao.ativos - situacao.semEmail.length} com e-mail`}
        />
      </Stack>

      {ultimoEnvio.detalhe && (
        <Alert
          severity={
            ultimoEnvio.status === 'enviado'
              ? situacao.semEmail.length > 0
                ? 'warning'
                : 'success'
              : ultimoEnvio.status === 'falhou'
                ? 'error'
                : 'warning'
          }
          sx={{ mb: 2 }}
        >
          <AlertTitle>Último envio</AlertTitle>
          {ultimoEnvio.detalhe}
        </Alert>
      )}

      {nuncaTentou && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Nenhum envio foi tentado ainda. O primeiro acontece no próximo cadastro de colaborador
          ou na próxima passada da rotina automática.
        </Alert>
      )}

      {situacao.impedimentos.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>
            {situacao.impedimentos.length === 1
              ? 'Um impedimento para o próximo envio'
              : `${situacao.impedimentos.length} impedimentos para o próximo envio`}
          </AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {situacao.impedimentos.map((texto) => (
              <li key={texto}>
                <Typography variant="body2">{texto}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {situacao.semEmail.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>
            {situacao.semEmail.length} colaborador(es) ativo(s) ficam de fora do clube
          </AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            O clube identifica a pessoa pelo e-mail. Sem e-mail no cadastro, o colaborador não
            entra no lote — e continua normalmente aqui no Lidera SST. Cadastre o e-mail e a
            rotina automática o inclui no próximo envio.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {situacao.semEmail.map((pessoa) => (
              <Link key={pessoa.id} href={`/funcionarios/${pessoa.id}`} underline="hover">
                <Chip size="small" variant="outlined" label={pessoa.nome} clickable />
              </Link>
            ))}
          </Stack>
        </Alert>
      )}

      {situacao.pronto && situacao.semEmail.length === 0 && ultimoEnvio.status === 'enviado' && (
        <Alert severity="success">
          Tudo certo: a empresa está provisionada e todos os colaboradores ativos foram enviados.
        </Alert>
      )}
    </ProfileSection>
  );
};

export default ClubeLideraStatusCard;
